import React from 'react'
import type { RacingBetType, Acceptor, Race } from '@mobi/betslip/types'
import { toMoney } from '@mobi/utils/money'
import {
  isFobMatchedSelection,
  isFobPropositionSelection,
  isSameRaceMultiSelection,
  isStartingPriceMatchedSelection,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import { toTitleCase } from '@mobi/utils/string'
import type { MysteryAcceptor } from '@core/Data/Racing/acceptors'
import type { QuickbetState } from '@core/Areas/Quickbet/driver'
import { SelectionInfo } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/Common/SelectionInfo'
import { getAcceptorNameForSingleSelection } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/BetInfoHeader/helpers'
import { MultiBetLegPriceStyled } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/Common/BetSelectionInfo.styles'
import { NoveltySelection, MultiRaceSelection, QuickbetSilkImage } from './Components'

export const SelectionDisplay: React.FC<
  {
    races: Race[]
    acceptors: Acceptor[]
    multiBetLegType?: 'Win' | 'Place'
  } & Pick<QuickbetState, 'selection'>
> = ({ selection, races, acceptors, multiBetLegType }) => {
  if (!selection) {
    return null
  }

  if (isToteSelection(selection)) {
    if (isSingleRaceNoveltyBetType(selection.betType)) {
      return <NoveltySelection selection={selection} acceptors={acceptors} />
    }

    if (isMultiRaceBetType(selection.betType)) {
      return <MultiRaceSelection selection={selection} races={races} acceptors={acceptors} />
    }

    return (
      <SelectionInfo
        leftSlot={
          <QuickbetSilkImage
            acceptor={extractSelectedAcceptor(selection.selectionString, acceptors)}
          />
        }
        text={
          <span data-tid-selection-text>
            {getAcceptorNameForSingleSelection(selection.selectionString, acceptors)}
          </span>
        }
      />
    )
  }

  if (isSameRaceMultiSelection(selection)) {
    const sorted = selection.acceptors
      .map(a => ({
        ...a,
        legName: a.legNumber === 0 ? 'WIN' : `TOP ${a.legNumber + 1}`,
      }))
      .sort(sameRaceMultiSortCallback)
    const result = sorted.map(selectionAcceptor => {
      const number = selectionAcceptor.acceptorNumber.toString()

      return (
        <SelectionInfo
          key={selectionAcceptor.legName}
          leftSlot={<QuickbetSilkImage acceptor={extractSelectedAcceptor(number, acceptors)} />}
          text={getAcceptorNameForSingleSelection(number, acceptors)}
          rightSlot={selectionAcceptor.legName}
        />
      )
    })
    return result
  }

  if (isFobMatchedSelection(selection)) {
    return (
      <SelectionInfo
        leftSlot={
          <QuickbetSilkImage
            acceptor={extractSelectedAcceptor(selection.acceptorNumber.toString(), acceptors)}
          />
        }
        text={getAcceptorNameForSingleSelection(selection.acceptorNumber.toString(), acceptors)}
        subText={multiBetLegType}
        rightSlot={
          multiBetLegType ? (
            <MultiBetLegPriceStyled>
              {toMoney(
                multiBetLegType === 'Win' ? selection.winPrice : (selection.placePrice ?? 0)
              )}
            </MultiBetLegPriceStyled>
          ) : undefined
        }
      />
    )
  }

  if (isStartingPriceMatchedSelection(selection)) {
    const acceptor = selection.selectedAcceptor
    if (acceptor) {
      return (
        <SelectionInfo
          leftSlot={<QuickbetSilkImage acceptor={acceptor} />}
          text={getAcceptorTextForSingleSelection(acceptor)}
        />
      )
    }
  }

  if (isFobPropositionSelection(selection)) {
    const acceptor = selection.selectedAcceptors && selection.selectedAcceptors[0]

    if (acceptor) {
      return (
        <SelectionInfo
          leftSlot={<QuickbetSilkImage acceptor={acceptor} />}
          text={getAcceptorTextForSingleSelection(acceptor)}
          subText={multiBetLegType}
          rightSlot={
            multiBetLegType ? (
              <MultiBetLegPriceStyled>
                {toMoney(
                  multiBetLegType === 'Win' ? selection.winPrice : (selection.placePrice ?? 0)
                )}
              </MultiBetLegPriceStyled>
            ) : undefined
          }
        />
      )
    } else {
      return (
        <SelectionInfo
          text={selection.displayName}
          subText={multiBetLegType}
          rightSlot={
            multiBetLegType ? (
              <MultiBetLegPriceStyled>
                {toMoney(
                  multiBetLegType === 'Win' ? selection.winPrice : (selection.placePrice ?? 0)
                )}
              </MultiBetLegPriceStyled>
            ) : undefined
          }
        />
      )
    }
  }

  if (PRODUCTION) return null

  return <span>{JSON.stringify(selection)}</span>
}

// =============
// Local Helpers
// =============

function sameRaceMultiSortCallback(
  a: SameRaceMultiSortCallbackParamType,
  b: SameRaceMultiSortCallbackParamType
) {
  const diff = a.legNumber - b.legNumber

  // lower leg number must come first
  if (diff != 0) return diff

  // if same leg, lower acceptor number must come first
  // we never expect same acceptor number
  return a.acceptorNumber - b.acceptorNumber
}

function extractSelectedAcceptor(
  selectionNumber: string,
  acceptors: Acceptor[]
): Acceptor | undefined {
  return acceptors.find(a => `${a.number}` === selectionNumber)
}

function isSingleRaceNoveltyBetType(value: RacingBetType): boolean {
  const noveltyTypes: RacingBetType[] = ['Exacta', 'Quinella', 'Trifecta', 'First 4']
  return noveltyTypes.includes(value)
}

function isMultiRaceBetType(value: RacingBetType): boolean {
  return value === 'Double' || value === 'Quaddie' || value === 'All Up'
}

function getAcceptorTextForSingleSelection(acceptor: MysteryAcceptor): string | null {
  if (!acceptor) {
    return null
  }
  return acceptor.number + ' - ' + toTitleCase(acceptor.name)
}

// =====
// Types
// =====

type SameRaceMultiSortCallbackParamType = { legNumber: number; acceptorNumber: number }
